import React from "react"
import { isHoliday } from "feiertagejs"
import { isHolidayToday } from "utils/holidays"
import dayjs from "dayjs"

// components
import {
  Tag,
  Row,
  Space,
  Typography,
  Button,
  List,
  Divider,
  message,
} from "antd"
import { ClockCircleFilled, ArrowRightOutlined } from "@ant-design/icons"

// configs
import config from "config"

// context
import { useResto } from "store/resto"
import { useTranslation } from "react-i18next"
import { useOrders } from "store/orders"
import { useFunnel } from "store/funnel"

// utils
import { groupBy } from "lodash-es"
import { matchTimeRanges } from "utils"

// custom
import OrderItem from "components/OrderItem"

// utils
import { formatAsCurrency } from "utils/currency"

const OrderDrawer = ({ onFinish }) => {
  const orders = useOrders()
  const funnel = useFunnel()
  const restoSettings = useResto()
  const { t } = useTranslation()

  const [messageApi, contextHolder] = message.useMessage()
  const [loading, setLoading] = React.useState(false)

  const holidayToday = React.useMemo(() => {
    return isHolidayToday()
  }, [])

  return (
    <div>
      {contextHolder}

      <Typography.Paragraph>{t("select_dishes")}</Typography.Paragraph>

      <List
        data-testid="order-list"
        size="small"
        header={<OrderItem titles />}
        dataSource={Object.entries(groupBy(orders.order.list, "hash"))}
        locale={{ emptyText: t("no_items") }}
        renderItem={([id, list]) => (
          <List.Item>
            <OrderItem
              list={list}
              onRemove={(itemId) => {
                orders.order.remove(itemId)
                funnel.add("remove_item")
                messageApi.success(t("item_removed"))
              }}
            />
          </List.Item>
        )}
      />

      <Row justify="end">
        <Typography.Text strong style={{ marginRight: 16 }}>
          {t("subtotal")}: {formatAsCurrency(orders.order.total)}
        </Typography.Text>
      </Row>

      <Divider />

      {((restoSettings && restoSettings.restaurent_is_closed) ||
        !matchTimeRanges(config.hours)) && (
        <>
          <Typography.Paragraph type="danger">
            <Typography.Text strong type="danger">
              {t("attention")}
            </Typography.Text>{" "}
            {t("attention_note")}
          </Typography.Paragraph>

          <Typography.Paragraph type="danger">
            {holidayToday ? (
              <Tag color="error" icon={<ClockCircleFilled />}>
                <Space>
                  <Typography.Text strong>{t("today_colon")}</Typography.Text>
                  <Typography.Text strong>{t("holiday")}</Typography.Text>
                </Space>
              </Tag>
            ) : (
              <Tag color="error" icon={<ClockCircleFilled />}>
                <Space>
                  <Typography.Text strong>{t("today")}</Typography.Text>
                  {config.hours[new Date().getDay()] ? (
                    <span>
                      {config.hours[new Date().getDay()].map(
                        ({ start_time, end_time }) => (
                          <Tag
                            key={dayjs(start_time).format("HH:mm")}
                          >{`${dayjs(start_time).format("HH:mm")} - ${dayjs(
                            end_time
                          ).format("HH:mm")}`}</Tag>
                        )
                      )}
                    </span>
                  ) : (
                    t("closed")
                  )}
                </Space>
              </Tag>
            )}
          </Typography.Paragraph>
        </>
      )}

      <Row justify="end">
        <Button
          disabled={orders.order.total === 0}
          loading={loading}
          type="primary"
          onClick={async () => {
            setLoading(true)
            try {
              await onFinish()
            } finally {
              setLoading(false)
            }
          }}
        >
          {t("next")}
          <ArrowRightOutlined />
        </Button>
      </Row>
    </div>
  )
}

export default OrderDrawer
